/**
 * The component state contract: loading, empty, partial, stale, offline,
 * delayed, unauthorized, no-data and low-confidence. Each one keeps context and
 * offers a concrete recovery action; none of them wipes the screen.
 */
import type { ReactNode } from 'react';
import type { SemanticTone } from '@dcc/shared';
import { ApiRequestError } from '../lib/api';
import type { LiveConnection } from '../hooks/useLive';
import { relativeTime } from '../lib/format';

export function SkeletonPanel({ rows = 3, title = true }: { rows?: number; title?: boolean }) {
  return (
    <div className="panel" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {title && <div className="skeleton skeleton--title" style={{ marginBottom: 16 }} />}
      <div className="stack">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="skeleton skeleton--line" style={{ width: `${92 - i * 11}%` }} />
        ))}
      </div>
    </div>
  );
}

export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid--tiles" aria-busy="true">
      <span className="sr-only">Loading projects</span>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="panel">
          <div className="skeleton skeleton--title" style={{ marginBottom: 14 }} />
          <div className="skeleton skeleton--block" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  tone = 'neutral',
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
  tone?: SemanticTone;
}) {
  return (
    <div className="state" data-tone={tone}>
      <h3 className="state__title">{title}</h3>
      <p className="state__body">{body}</p>
      {action}
    </div>
  );
}

/**
 * "No data" and "no connected source" are different answers to the operator's
 * question, so they are never collapsed into one empty state.
 */
export function NoDataState({
  what,
  connected,
  connectAction,
}: {
  what: string;
  connected: boolean;
  connectAction?: ReactNode;
}) {
  return connected ? (
    <EmptyState
      title={`No ${what} yet`}
      body={`The source is connected and reporting, and it has produced no ${what} so far. This is a real empty result, not a missing connection.`}
    />
  ) : (
    <EmptyState
      tone="attention"
      title={`${what} cannot be observed`}
      body={`No source is connected for this project, so DCC has nothing to report about ${what}. Connecting a source is what makes this observable.`}
      action={connectAction}
    />
  );
}

export function ErrorState({
  error,
  onRetry,
  context,
}: {
  error: Error;
  onRetry?: () => void;
  context?: string;
}) {
  const api = error instanceof ApiRequestError ? error : null;
  if (api?.status === 401) {
    return (
      <EmptyState
        tone="attention"
        title="Your session has ended"
        body="Sign in again to continue. Nothing you were looking at has changed."
        action={
          <a className="btn btn--primary" href="/login">
            Sign in
          </a>
        }
      />
    );
  }
  if (api?.status === 403) {
    return (
      <EmptyState
        tone="attention"
        title="Not permitted for your role"
        body={`Your role does not include this action${context ? ` (${context})` : ''}. Ask a project owner or tenant admin if you need it.`}
      />
    );
  }
  return (
    <div className="state" data-tone="risk">
      <h3 className="state__title">This did not load</h3>
      <p className="state__body">{api?.message ?? error.message}</p>
      {api && (
        <p className="tiny dim mono">
          reference {api.correlationId} · {api.code}
        </p>
      )}
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Shown above still-useful data whose last refresh failed. */
export function DegradedBanner({
  onRetry,
  loadedAt,
}: {
  onRetry: () => void;
  loadedAt: string | null;
}) {
  return (
    <div className="banner" data-tone="attention" role="status">
      <span className="chip__glyph" aria-hidden="true">
        !
      </span>
      <span className="banner__text">
        Showing the last data DCC could load{loadedAt ? `, from ${relativeTime(loadedAt)}` : ''}.
        The most recent refresh did not complete, so this may already be out of date.
      </span>
      <button type="button" className="btn btn--sm" onClick={onRetry}>
        Refresh
      </button>
    </div>
  );
}

/** Live-connection health. An operator must see when updates have stopped. */
export function LiveBadge({
  connection,
  lastEventAt,
}: {
  connection: LiveConnection;
  lastEventAt: string | null;
}) {
  const tone: SemanticTone =
    connection === 'live'
      ? 'success'
      : connection === 'connecting'
        ? 'active'
        : connection === 'reconnecting'
          ? 'attention'
          : 'risk';
  const label =
    connection === 'live'
      ? 'Live'
      : connection === 'connecting'
        ? 'Connecting'
        : connection === 'reconnecting'
          ? 'Reconnecting'
          : 'Not receiving updates';
  return (
    <span className="chip" data-tone={tone} role="status" aria-live="polite">
      <span
        className={connection === 'live' ? 'dot dot--live' : 'dot'}
        data-tone={tone}
        aria-hidden="true"
      />
      {label}
      {connection === 'live' && lastEventAt && (
        <span className="dim"> · {relativeTime(lastEventAt)}</span>
      )}
    </span>
  );
}

export function LowConfidenceNote({ reasons }: { reasons: string[] }) {
  if (reasons.length === 0) return null;
  return (
    <details className="state lowconf" data-tone="risk">
      <summary className="state__title" style={{ cursor: 'pointer' }}>
        Why this forecast is uncertain
      </summary>
      <ul className="stack stack--tight small" style={{ margin: '12px 0 0', paddingLeft: 18 }}>
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </details>
  );
}
